import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { money, format } from "@/publicjs/money"; //? Formateo y obtencion de moneda configurada de la empresa
import { errorActions } from "@/alerts"; //? Alertas de error, etc
import { formatDate, formatDateTime } from "@/publicjs/formatDate"; //? Retorna la fecha y hora actual
import titlesConfig from "/public/apis/config.json"; //? Títulos de configuracion segun pais
import { convertCurrencyToLetters } from "@/publicjs/convertCurrencyToLetters"; //? Convierte moneda a letras
import styles from "@/publicjs/styles"; //? Estilos como colores de texto, color de fondos y tamaño de textos
import createElementPDF from "@/publicjs/createElementPDF";
import { loadSalePrintContext } from "@/components/Sales/PrintVoucher/printContext";
pdfMake.vfs = pdfFonts.pdfMake.vfs;

const getVerificationQrValue = (registers) => {
  const qrValue = registers?.orden?.url_signed;

  if (typeof qrValue !== "string") {
    return null;
  }

  const normalizedQrValue = qrValue.trim();
  return /^https?:\/\//i.test(normalizedQrValue) ? normalizedQrValue : null;
};

const getCustomerPhone = (registers) => {
  const candidates = [
    registers?.cliente?.telefono,
    registers?.cliente?.celular,
    registers?.cliente?.telefonos,
    registers?.cliente?.whatsapp,
  ];

  return (
    candidates.find(
      (value) => typeof value === "string" && value.trim().length > 0,
    ) ?? ""
  );
};

const getTotalItems = (registers) => {
  const total = Array.isArray(registers?.detalle)
    ? registers.detalle.reduce((sum, register) => {
        const quantity = Number(
          register?.cantidad_impresion ?? register?.cantidad ?? 0,
        );

        return Number.isFinite(quantity) ? sum + quantity : sum;
      }, 0)
    : 0;

  return Number.isInteger(total) ? String(total) : total.toFixed(2);
};

const getVoucherPrintLabel = (registers) =>
  registers?.orden?.serie_fe ? "e-NCF" : "NCF";

const getVoucherPrintCode = (registers) =>
  `${registers?.orden?.serie_fe ?? registers?.orden?.serie_comprobante ?? ""}${
    registers?.orden?.numero_comprobante ?? ""
  }`;

const buildSignedInvoiceQrStack = (registers, qrFit = 104) => {
  const qrValue = getVerificationQrValue(registers);

  return [
    {
      text: qrValue ? "CONSULTA DGII" : "QR DGII NO DISPONIBLE",
      fontSize: 7,
      color: styles.colors.colorBlue,
      bold: true,
      alignment: "center",
      margin: [0, 0, 0, 4],
    },
    qrValue
      ? {
          qr: qrValue,
          fit: qrFit,
          alignment: "center",
          foreground: styles.colors.colorDescription,
          background: styles.colors.white,
          margin: [0, 0, 0, 6],
        }
      : {
          text: "El comprobante aun no tiene una URL valida de verificacion.",
          fontSize: 7,
          color: styles.colors.colorDescription,
          alignment: "center",
          margin: [8, 10, 8, 10],
        },
    {
      text: registers.orden.code_signed ? "CODIGO DE SEGURIDAD" : "",
      fontSize: 6.5,
      color: styles.colors.colorBlue,
      bold: true,
      alignment: "center",
      margin: [0, 2, 0, 1],
    },
    {
      text: registers.orden.code_signed ?? "",
      fontSize: 8,
      color: styles.colors.colorTitle,
      bold: true,
      alignment: "center",
      margin: [0, 0, 0, 4],
    },
    {
      text: qrValue ? "Escanee para validar el e-CF directamente en DGII." : "",
      fontSize: 6.5,
      color: styles.colors.colorDescription,
      alignment: "center",
      margin: [4, 0, 4, 4],
    },
    {
      text: registers.orden.date_signed ? "FECHA Y HORA DE FIRMA" : "",
      fontSize: 6.5,
      color: styles.colors.colorBlue,
      bold: true,
      alignment: "center",
      margin: [0, 0, 0, 1],
    },
    {
      text: registers.orden.date_signed
        ? formatDateTime(registers.orden.date_signed)
        : "",
      fontSize: 7.5,
      color: styles.colors.colorDescription,
      alignment: "center",
      margin: [0, 0, 0, 0],
    },
  ];
};

const buildOfferPriceInfo = (register) => {
  const previousPrice = Number(register.precio_anterior_impresion ?? 0);
  const offerPrice = Number(register.precio_oferta_impresion ?? 0);

  if (
    !Number.isFinite(previousPrice) ||
    !Number.isFinite(offerPrice) ||
    previousPrice <= 0 ||
    offerPrice <= 0 ||
    previousPrice <= offerPrice
  ) {
    return null;
  }

  return `OFERTA: antes ${money()}${format(
    previousPrice,
  )}, ahora ${money()}${format(offerPrice)}`;
};

const buildCompactProductInfo = (register) => {
  const info = [
    register.product_serie !== null ? `S=${register.product_serie}` : null,
    register.vencimiento !== null ? `V=${register.vencimiento}` : null,
    buildOfferPriceInfo(register),
    register.detalle_promocion_impresion ?? null,
  ].filter(Boolean);

  return info.length > 0 ? `\n${info.join(" | ")}` : "";
};

//* Función para imprimir el documento
/**
 * @param {*} idOrder | ID de la ultima orden realizada
 */
export const printVoucherOnLetter = async (idOrder) => {
  const { company, registers, base64Image } = await loadSalePrintContext(
    idOrder,
  );
  if (registers.length <= 0) {
    errorActions(
      "Lo sentimos hemos tenido unos incoveniente al generar este comprobante",
    );
  }
  const saleTypeDescription = (registers.tipo_de_venta?.descripcion ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();
  const initialPayment = parseFloat(registers.abono ?? 0) || 0;
  const customerPaidTotal =
    parseFloat(registers.orden.pago_en_efectivo) +
    parseFloat(registers.orden.pago_en_tarjeta) +
    parseFloat(registers.orden.pago_en_transferencia) +
    parseFloat(registers.orden.pago_en_cheque);
  const pendingPayment = Math.max(
    0,
    (parseFloat(registers.orden.total ?? 0) || 0) - initialPayment,
  );
  const showInitialPayment =
    saleTypeDescription.includes("CREDITO") || initialPayment > 0;
  const showPendingPayment = saleTypeDescription.includes("CREDITO");

  const buildSignedInvoiceQrBlock = () => ({
    table: {
      widths: ["*"],
      body: [
        [
          {
            stack: buildSignedInvoiceQrStack(registers),
            border: [false, false, false, false],
          },
        ],
      ],
    },
    layout: {
      hLineWidth: () => 0,
      vLineWidth: () => 0,
    },
    margin: [10, 10, 10, 8],
  });

  const buildStandaloneQrSection = () => ({
    columns: [
      {
        width: 215,
        table: {
          widths: ["*"],
          body: [
            [
              {
                stack: [buildSignedInvoiceQrBlock()],
                border: [true, true, true, true],
              },
            ],
          ],
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
        },
      },
      {
        width: "*",
        text: "",
        border: [false, false, false, false],
      },
    ],
    columnGap: 12,
    margin: [0, 12, 0, 10],
  });

  const buildTotalsSection = () => ({
    columns: [
      {
        width: "*",
        text: "",
      },
      {
        width: 245,
        table: {
          headerRows: 0,
          widths: ["*", 80],
          body: buildTotalsSummaryBody(),
        },
        layout: {
          hLineWidth: () => 0,
          vLineWidth: () => 0,
          paddingLeft: () => 0,
          paddingRight: () => 0,
          paddingTop: () => 0,
          paddingBottom: () => 0,
        },
      },
    ],
    columnGap: 12,
    margin: [0, 0, 0, 0],
  });

  const buildTotalsSummaryBody = () =>
    totalData.map((row) => {
      if (row[0]?.colSpan === 3) {
        return [
          {
            ...row[0],
            colSpan: 2,
          },
          "",
        ];
      }

      return [row[1], row[2]];
    });
  let totalData = [];

  if (parseFloat(registers.orden.total_nota_de_credito) > 0) {
    totalData.push([
      {},
      {
        text: "Divisa:",
        border: [false, true, false, false],
        margin: [0, 5, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
        bold: true,
      },
      {
        text:
          registers.orden.descripcion_moneda.length > 10
            ? registers.orden.descripcion_moneda.slice(0, 10) + "***"
            : registers.orden.descripcion_moneda,
        border: [false, true, true, false],
        margin: [0, 5, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
      },
    ]);
    totalData.push([
      "",
      {
        text: "Descuentos",
        border: [false, false, false, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
        bold: true,
      },
      {
        text: registers.orden.descuento + " %",
        border: [false, false, true, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
      },
    ]);
    totalData.push([
      "",
      {
        text: "Otros descuentos",
        border: [false, false, false, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
        bold: true,
      },
      {
        text: money() + format(registers.orden.otros_descuentos),
        border: [false, false, true, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
      },
    ]);
    totalData.push([
      "",
      {
        text: "Nota de Crédito",
        border: [false, false, false, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
        bold: true,
      },
      {
        text: money() + " " + format(registers.orden.total_nota_de_credito),
        border: [false, false, true, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
      },
    ]);
    totalData.push([
      "",
      {
        text: "Subtotal",
        border: [false, false, false, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
        bold: true,
      },
      {
        text: money() + " " + format(registers.orden.subtotal),
        border: [false, false, true, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
      },
    ]);
    totalData.push([
      "",
      {
        text: titlesConfig.iva + " " + registers.orden.igv_aplicado + "%",
        border: [false, false, false, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
        bold: true,
      },
      {
        text: money() + " " + format(registers.orden.igv),
        border: [false, false, true, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
      },
    ]);
    totalData.push([
      "",
      {
        text: "Total",
        border: [false, false, false, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
        bold: true,
      },
      {
        text: money() + " " + format(registers.orden.total),
        border: [false, false, true, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
      },
    ]);
    totalData.push([
      "",
      {
        text: "El cliente paga",
        border: [false, false, false, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
        bold: true,
      },
      {
        text: money() + " " + format(customerPaidTotal),
        border: [false, false, true, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
      },
    ]);
    if (showInitialPayment) {
      totalData.push([
        "",
        {
          text: "Abono inicial (aplica en venta a credito)",
          border: [false, false, false, false],
          margin: [0, 0, 0, 0],
          valign: "middle",
          alignment: "right",
          fontSize: styles.fontSize.pdf.sm,
          color: styles.colors.colorTitle,
          bold: true,
        },
        {
          text: money() + " " + format(initialPayment),
          border: [false, false, true, false],
          margin: [0, 0, 0, 0],
          valign: "middle",
          alignment: "right",
          fontSize: styles.fontSize.pdf.sm,
          color: styles.colors.colorTitle,
        },
      ]);
    }
    if (showPendingPayment) {
      totalData.push([
        "",
        {
          text: "Saldo pendiente",
          border: [false, false, false, false],
          margin: [0, 0, 0, 0],
          valign: "middle",
          alignment: "right",
          fontSize: styles.fontSize.pdf.sm,
          color: styles.colors.colorTitle,
          bold: true,
        },
        {
          text: money() + " " + format(pendingPayment),
          border: [false, false, true, false],
          margin: [0, 0, 0, 0],
          valign: "middle",
          alignment: "right",
          fontSize: styles.fontSize.pdf.sm,
          color: styles.colors.colorTitle,
        },
      ]);
    }
    totalData.push([
      {
        text: convertCurrencyToLetters(
          parseFloat(registers.orden.total),
        ).toUpperCase(),
        border: [true, false, true, true],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.xsm,
        color: styles.colors.colorDescription,
        bold: true,
        colSpan: 3,
      },
      "",
      "",
    ]);
  } else if (parseFloat(registers.orden.monto_a_pagar_empresa_por_seguro) > 0) {
    totalData.push([
      {},
      {
        text: "Divisa:",
        border: [false, true, false, false],
        margin: [0, 5, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
        bold: true,
      },
      {
        text:
          registers.orden.descripcion_moneda.length > 10
            ? registers.orden.descripcion_moneda.slice(0, 10) + "***"
            : registers.orden.descripcion_moneda,
        border: [false, true, true, false],
        margin: [0, 5, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
      },
    ]);
    totalData.push([
      "",
      {
        text: "Descuentos",
        border: [false, false, false, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
        bold: true,
      },
      {
        text: registers.orden.descuento + " %",
        border: [false, false, true, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
      },
    ]);
    totalData.push([
      "",
      {
        text: "Otros descuentos",
        border: [false, false, false, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
        bold: true,
      },
      {
        text: money() + format(registers.orden.otros_descuentos),
        border: [false, false, true, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
      },
    ]);
    totalData.push([
      "",
      {
        text: "Subtotal",
        border: [false, false, false, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
        bold: true,
      },
      {
        text: money() + " " + format(registers.orden.subtotal),
        border: [false, false, true, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
      },
    ]);
    totalData.push([
      "",
      {
        text: titlesConfig.iva + " " + registers.orden.igv_aplicado + "%",
        border: [false, false, false, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
        bold: true,
      },
      {
        text: money() + " " + format(registers.orden.igv),
        border: [false, false, true, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
      },
    ]);
    totalData.push([
      "",
      {
        text: "Total",
        border: [false, false, false, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
        bold: true,
      },
      {
        text: money() + " " + format(registers.orden.total),
        border: [false, false, true, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
      },
    ]);
    totalData.push([
      "",
      {
        text: "Aseguradora",
        border: [false, false, false, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
        bold: true,
      },
      {
        text:
          money() +
          " " +
          format(registers.orden.monto_a_pagar_empresa_por_seguro),
        border: [false, false, true, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
      },
    ]);
    totalData.push([
      "",
      {
        text: "El cliente paga",
        border: [false, false, false, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
        bold: true,
      },
      {
        text: money() + " " + format(customerPaidTotal),
        border: [false, false, true, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
      },
    ]);
    if (showInitialPayment) {
      totalData.push([
        "",
        {
          text: "Abono inicial (aplica en venta a credito)",
          border: [false, false, false, false],
          margin: [0, 0, 0, 0],
          valign: "middle",
          alignment: "right",
          fontSize: styles.fontSize.pdf.sm,
          color: styles.colors.colorTitle,
          bold: true,
        },
        {
          text: money() + " " + format(initialPayment),
          border: [false, false, true, false],
          margin: [0, 0, 0, 0],
          valign: "middle",
          alignment: "right",
          fontSize: styles.fontSize.pdf.sm,
          color: styles.colors.colorTitle,
        },
      ]);
    }
    if (showPendingPayment) {
      totalData.push([
        "",
        {
          text: "Saldo pendiente",
          border: [false, false, false, false],
          margin: [0, 0, 0, 0],
          valign: "middle",
          alignment: "right",
          fontSize: styles.fontSize.pdf.sm,
          color: styles.colors.colorTitle,
          bold: true,
        },
        {
          text: money() + " " + format(pendingPayment),
          border: [false, false, true, false],
          margin: [0, 0, 0, 0],
          valign: "middle",
          alignment: "right",
          fontSize: styles.fontSize.pdf.sm,
          color: styles.colors.colorTitle,
        },
      ]);
    }
    totalData.push([
      {
        text: convertCurrencyToLetters(
          parseFloat(registers.orden.total),
        ).toUpperCase(),
        border: [true, false, true, true],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.xsm,
        color: styles.colors.colorDescription,
        bold: true,
        colSpan: 3,
      },
      "",
      "",
    ]);
  } else {
    totalData.push([
      {},
      {
        text: "Divisa:",
        border: [false, true, false, false],
        margin: [0, 5, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
        bold: true,
      },
      {
        text:
          registers.orden.descripcion_moneda.length > 10
            ? registers.orden.descripcion_moneda.slice(0, 10) + "***"
            : registers.orden.descripcion_moneda,
        border: [false, true, true, false],
        margin: [0, 5, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
      },
    ]);
    totalData.push([
      "",
      {
        text: "Descuentos",
        border: [false, false, false, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
        bold: true,
      },
      {
        text: registers.orden.descuento + " %",
        border: [false, false, true, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
      },
    ]);
    totalData.push([
      "",
      {
        text: "Otros descuentos",
        border: [false, false, false, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
        bold: true,
      },
      {
        text: money() + format(registers.orden.otros_descuentos),
        border: [false, false, true, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
      },
    ]);
    totalData.push([
      "",
      {
        text: "Subtotal",
        border: [false, false, false, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
        bold: true,
      },
      {
        text: money() + " " + format(registers.orden.subtotal),
        border: [false, false, true, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
      },
    ]);
    totalData.push([
      "",
      {
        text: titlesConfig.iva + " " + registers.orden.igv_aplicado + "%",
        border: [false, false, false, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
        bold: true,
      },
      {
        text: money() + " " + format(registers.orden.igv),
        border: [false, false, true, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
      },
    ]);
    totalData.push([
      "",
      {
        text: "Total",
        border: [false, false, false, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
        bold: true,
      },
      {
        text: money() + " " + format(registers.orden.total),
        border: [false, false, true, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
      },
    ]);
    totalData.push([
      "",
      {
        text: "El cliente paga",
        border: [false, false, false, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
        bold: true,
      },
      {
        text: money() + " " + format(customerPaidTotal),
        border: [false, false, true, false],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.sm,
        color: styles.colors.colorTitle,
      },
    ]);
    if (showInitialPayment) {
      totalData.push([
        "",
        {
          text: "Abono inicial (aplica en venta a credito)",
          border: [false, false, false, false],
          margin: [0, 0, 0, 0],
          valign: "middle",
          alignment: "right",
          fontSize: styles.fontSize.pdf.sm,
          color: styles.colors.colorTitle,
          bold: true,
        },
        {
          text: money() + " " + format(initialPayment),
          border: [false, false, true, false],
          margin: [0, 0, 0, 0],
          valign: "middle",
          alignment: "right",
          fontSize: styles.fontSize.pdf.sm,
          color: styles.colors.colorTitle,
        },
      ]);
    }
    if (showPendingPayment) {
      totalData.push([
        "",
        {
          text: "Saldo pendiente",
          border: [false, false, false, false],
          margin: [0, 0, 0, 0],
          valign: "middle",
          alignment: "right",
          fontSize: styles.fontSize.pdf.sm,
          color: styles.colors.colorTitle,
          bold: true,
        },
        {
          text: money() + " " + format(pendingPayment),
          border: [false, false, true, false],
          margin: [0, 0, 0, 0],
          valign: "middle",
          alignment: "right",
          fontSize: styles.fontSize.pdf.sm,
          color: styles.colors.colorTitle,
        },
      ]);
    }
    totalData.push([
      {
        text: convertCurrencyToLetters(
          parseFloat(registers.orden.total),
        ).toUpperCase(),
        border: [true, false, true, true],
        margin: [0, 0, 0, 0],
        valign: "middle",
        alignment: "right",
        fontSize: styles.fontSize.pdf.xsm,
        color: styles.colors.colorDescription,
        bold: true,
        colSpan: 3,
      },
      "",
      "",
    ]);
  }

  try {
    let docDefinition = null;
    docDefinition = {
      // a string or { width: number, height: number }
      pageSize: "LETTER",

      // by default we use portrait, you can change it to landscape if you wish
      pageOrientation: "portrait", //cambia a [landscape] para imprimir de forma horizontal

      // [left, top, right, bottom] or [horizontal, vertical] or just a number for equal margins
      pageMargins: [24, 140, 24, 24],
      header: function (currentPage, pageCount, pageSize) {
        const companyBranchesCount = Number(company.numeroDeSucursales ?? 0);
        const storeNameUpper = (
          registers?.sucursal?.razon_social ?? ""
        ).toUpperCase();
        const companyNameUpper = (company.companyName ?? "").toUpperCase();
        const showStoreBlock =
          companyBranchesCount > 1 ||
          (storeNameUpper &&
            companyNameUpper &&
            storeNameUpper !== companyNameUpper);

        return [
          {
            columns: [
              {
                width: 100,
                stack: [
                  { image: base64Image, fit: [80, 80] }, // logo
                ],
                alignment: "left",
                margin: [0, 10, 0, 0],
              },
              {
                width: 285,
                stack: [
                  {
                    text: company.companyName,
                    fontSize: styles.fontSize.pdf.xl,
                    alignment: "left",
                    bold: true,
                    color: styles.colors.colorTitle,
                  },
                  {
                    text: "Dirección: " + company.companyAddress,
                    fontSize: styles.fontSize.pdf.xsm,
                    alignment: "left",
                    color: styles.colors.colorDescription,
                  },
                  {
                    text: "Tel:" + company.companycellphone,
                    fontSize: styles.fontSize.pdf.xsm,
                    alignment: "left",
                    color: styles.colors.colorDescription,
                  },
                  {
                    text: company.companySlogan,
                    fontSize: styles.fontSize.pdf.xsm,
                    alignment: "left",
                    color: styles.colors.colorDescription,
                  },
                  ...(showStoreBlock
                    ? [
                        {
                          text:
                            "Sucursal: ".toUpperCase() +
                            registers.sucursal.razon_social.toUpperCase(),
                          fontSize: styles.fontSize.pdf.xsm,
                          alignment: "left",
                          bold: true,
                          color: styles.colors.colorTitle,
                        },
                        {
                          text:
                            "DIR: " +
                            (registers.sucursal.direccion ?? "").toUpperCase(),
                          fontSize: styles.fontSize.pdf.xsm,
                          alignment: "left",
                          color: styles.colors.colorDescription,
                        },
                        {
                          text: "TEL: " + (registers.sucursal.celular ?? ""),
                          fontSize: styles.fontSize.pdf.xsm,
                          alignment: "left",
                          color: styles.colors.colorDescription,
                        },
                      ]
                    : []),
                ],
                alignment: "left",
                margin: [2, 10, 24, 0],
              },
              // Columna derecha para los datos de la empresa
              // Tabla para envolver los elementos y aplicar bordes
              {
                table: {
                  widths: ["*"],
                  body: [
                    [
                      {
                        stack: [
                          {
                            text:
                              titlesConfig.titleTypeDocument +
                              " " +
                              company.companyRUC,
                            fontSize: styles.fontSize.pdf.lg,
                            alignment: "center",
                            color: styles.colors.colorTitle,
                            bold: true,
                            margin: [0, 5, 0, 0],
                          },
                          {
                            text: registers.tipo_comprobante.descripcion,
                            fontSize: styles.fontSize.pdf.sm,
                            alignment: "center",
                            color: styles.colors.colorTitle,
                            bold: true,
                            margin: [0, 5, 0, 0],
                          },
                          {
                            text: `${getVoucherPrintLabel(
                              registers,
                            )}: ${getVoucherPrintCode(registers)}`,
                            fontSize: styles.fontSize.pdf.sm,
                            alignment: "center",
                            color: styles.colors.colorTitle,
                            bold: true,
                            margin: [0, 5, 0, 0],
                          },
                          {
                            text: `N° REF - ${registers.orden.numero_referencia_comprobante}`,
                            fontSize: styles.fontSize.pdf.sm,
                            alignment: "center",
                            color: styles.colors.colorTitle,
                            bold: true,
                            margin: [0, 5, 0, 0],
                          },
                          {
                            text:
                              registers.orden.vencimiento_secuencia !== null
                                ? `V. SECUENCIA: ${formatDate(
                                    registers.orden.vencimiento_secuencia,
                                  )}`
                                : "",
                            fontSize: styles.fontSize.pdf.sm,
                            alignment: "center",
                            color: styles.colors.colorTitle,
                            bold: true,
                            margin: [0, 5, 0, 0],
                          },
                        ],
                      },
                    ],
                  ],
                },
                layout: {
                  hLineWidth: function (i, node) {
                    // Agrega un borde horizontal en la parte superior del primer elemento,
                    // y en la parte inferior de todos los elementos
                    if (i === 0 || i === node.table.body.length) {
                      return 2;
                    }
                    // No agrega bordes horizontales para los elementos internos
                    return 0;
                  },
                  hLineColor: function () {
                    // Establece el color del borde horizontal
                    return styles.colors.borderColor;
                  },
                  vLineColor: function () {
                    // CEstablece el color del borde vertical
                    return styles.colors.borderColor;
                  },
                  vLineWidth: function () {
                    // Agrega un borde vertical en el lado derecho de cada elemento
                    return 2;
                  },
                },

                alignment: "right",
                margin: [0, 10, 24, 0],
              },
            ],
            margin: [24, 12, 0, 0], // Margen entre el encabezado y el contenido de la página
          },
          {
            canvas: [
              { type: "rect", x: 24, y: 90, w: pageSize.width - 48, h: 2 },
            ],
            margin: [0, 10], // Margen entre el rectángulo y el contenido de la página
          },
        ];
      },

      content: [
        //? Encabezado del comprobante con el nombre del cliente
        {
          table: {
            headerRows: 0,
            widths: [68, "*", 82, "*"], //ancho de la columnas
            body: [
              [
                {
                  text: "Cliente:",
                  border: [true, true, false, false],
                  fontSize: styles.fontSize.pdf.xsm,
                  margin: [0, 5, 0, 0],
                  valign: "middle",
                  color: styles.colors.colorTitle,
                  bold: true,
                },
                {
                  text: registers.orden.nombre_cliente_pedido,
                  border: [false, true, false, false],
                  fontSize: styles.fontSize.pdf.xsm,
                  margin: [-40, 5, 0, 0],
                  valign: "middle",
                  color: styles.colors.colorDescription,
                },
                {
                  text: "Dirección:",
                  border: [false, true, false, false],
                  margin: [0, 5, 0, 0],
                  fontSize: styles.fontSize.pdf.xsm,
                  valign: "middle",
                  color: styles.colors.colorTitle,
                  bold: true,
                },
                {
                  text: registers.cliente.direccion,
                  border: [false, true, true, false],
                  margin: [-40, 5, 0, 0],
                  fontSize: styles.fontSize.pdf.xsm,
                  valign: "middle",
                  color: styles.colors.colorDescription,
                },
              ],
              [
                {
                  text: "N° Documento:",
                  border: [true, false, false, false],
                  margin: [0, 0, 0, 0],
                  fontSize: styles.fontSize.pdf.xsm,
                  valign: "middle",
                  color: styles.colors.colorTitle,
                  bold: true,
                },
                {
                  text: registers.cliente.numero_documento,
                  border: [false, false, false, false],
                  margin: [0, 0, 0, 0],
                  fontSize: styles.fontSize.pdf.xsm,
                  valign: "middle",
                  color: styles.colors.colorDescription,
                },
                {
                  text: "Teléfono:",
                  border: [false, false, false, false],
                  margin: [0, 0, 0, 0],
                  fontSize: styles.fontSize.pdf.xsm,
                  valign: "middle",
                  color: styles.colors.colorTitle,
                  bold: true,
                },
                {
                  text: getCustomerPhone(registers),
                  border: [false, false, true, false],
                  margin: [0, 0, 0, 0],
                  fontSize: styles.fontSize.pdf.xsm,
                  valign: "middle",
                  color: styles.colors.colorDescription,
                },
              ],
              [
                {
                  text: "Fecha de emision:",
                  border: [true, false, false, false],
                  margin: [0, 0, 0, 0],
                  fontSize: styles.fontSize.pdf.xsm,
                  valign: "middle",
                  color: styles.colors.colorTitle,
                  bold: true,
                },
                {
                  text: formatDateTime(registers.orden.fecha_de_emision),
                  border: [false, false, false, false],
                  margin: [0, 0, 0, 0],
                  fontSize: styles.fontSize.pdf.xsm,
                  valign: "middle",
                  color: styles.colors.colorDescription,
                },
                {
                  text: "",
                  border: [false, false, false, false],
                },
                {
                  text: "",
                  border: [false, false, true, false],
                },
              ],
              [
                {
                  text: "Tipo de pago:",
                  border: [true, false, false, false],
                  margin: [0, 0, 0, 0],
                  fontSize: styles.fontSize.pdf.xsm,
                  valign: "middle",
                  color: styles.colors.colorTitle,
                  bold: true,
                },
                {
                  text: registers.tipo_de_pago.descripcion,
                  border: [false, false, false, false],
                  margin: [0, 0, 0, 0],
                  fontSize: styles.fontSize.pdf.xsm,
                  valign: "middle",
                  color: styles.colors.colorDescription,
                },
                {
                  text: "Tipo de venta:",
                  border: [false, false, false, false],
                  margin: [0, 0, 0, 0],
                  fontSize: styles.fontSize.pdf.xsm,
                  valign: "middle",
                  color: styles.colors.colorTitle,
                  bold: true,
                },
                {
                  text: registers.tipo_de_venta.descripcion,
                  border: [false, false, true, false],
                  margin: [0, 0, 0, 0],
                  fontSize: styles.fontSize.pdf.xsm,
                  valign: "middle",
                  color: styles.colors.colorDescription,
                },
              ],
              [
                {
                  text: "Nota adicional:",
                  border: [true, false, false, true],
                  margin: [0, 0, 0, 5],
                  fontSize: styles.fontSize.pdf.xsm,
                  valign: "middle",
                  color: styles.colors.colorTitle,
                  bold: true,
                },
                {
                  text: registers.orden.comentarios,
                  border: [false, false, true, true],
                  margin: [0, 0, 0, 5],
                  fontSize: styles.fontSize.pdf.xsm,
                  valign: "middle",
                  color: styles.colors.colorDescription,
                  colSpan: 3,
                },
                "",
                "",
              ],
              registers.orden?.codigo_pedido
                ? [
                    {
                      text: "Código pedido:",
                      border: [true, false, false, true],
                      margin: [0, 0, 0, 2],
                      fontSize: styles.fontSize.pdf.xsm,
                      valign: "middle",
                      color: styles.colors.colorTitle,
                      bold: true,
                    },
                    {
                      text: registers.orden.codigo_pedido,
                      border: [false, false, true, true],
                      margin: [0, 0, 0, 2],
                      fontSize: styles.fontSize.pdf.xsm,
                      valign: "middle",
                      color: styles.colors.colorDescription,
                      colSpan: 3,
                    },
                    "",
                    "",
                  ]
                : [
                    { text: "", border: [false, false, false, false] },
                    {
                      text: "",
                      border: [false, false, false, false],
                      colSpan: 3,
                    },
                    "",
                    "",
                  ],
            ],
          },

          borderRadius: [5, 5, 5, 5],
          layout: {
            hLineWidth: function (i, node) {
              return i === 0 || i === node.table.body.length ? 1 : 1;
            },
            vLineWidth: function (i, node) {
              return i === 0 || i === node.table.widths.length ? 1 : 1;
            },
            hLineColor: function (i, node) {
              return i === 0 || i === node.table.body.length
                ? styles.colors.borderColor
                : styles.colors.borderColor;
            },
            vLineColor: function (i, node) {
              return i === 0 || i === node.table.widths.length
                ? styles.colors.borderColor
                : styles.colors.borderColor;
            },
          },
        },
        //? Detalles del comprobante
        {
          table: {
            headerRows: 1,
            widths: [40, "*", 50, 50, 80, 50, 80], //ancho de la columnas
            body: [
              [
                {
                  text: "Cant.",
                  border: [true, true, false, true],
                  margin: [0, 2, 0, 2],
                  valign: "middle",
                  fontSize: styles.fontSize.pdf.sm,
                  color: styles.colors.colorTitle,
                  bold: true,
                },
                {
                  text: "Descripción",
                  border: [false, true, false, true],
                  margin: [0, 2, 0, 2],
                  valign: "middle",
                  fontSize: styles.fontSize.pdf.sm,
                  color: styles.colors.colorTitle,
                  bold: true,
                },
                {
                  text: "U.M",
                  border: [false, true, false, true],
                  margin: [0, 2, 0, 2],
                  valign: "middle",
                  fontSize: styles.fontSize.pdf.sm,
                  color: styles.colors.colorTitle,
                  bold: true,
                },
                {
                  text: "P.U",
                  border: [false, true, false, true],
                  margin: [0, 2, 0, 2],
                  valign: "middle",
                  fontSize: styles.fontSize.pdf.sm,
                  color: styles.colors.colorTitle,
                  bold: true,
                },
                {
                  text: "SubTotal",
                  border: [false, true, false, true],
                  margin: [0, 2, 0, 2],
                  valign: "middle",
                  fontSize: styles.fontSize.pdf.sm,
                  color: styles.colors.colorTitle,
                  bold: true,
                },
                {
                  text: titlesConfig.iva,
                  border: [false, true, false, true],
                  margin: [0, 2, 0, 2],
                  valign: "middle",
                  fontSize: styles.fontSize.pdf.sm,
                  color: styles.colors.colorTitle,
                  bold: true,
                },
                {
                  text: "Total",
                  border: [false, true, true, true],
                  margin: [0, 2, 0, 2],
                  valign: "middle",
                  fontSize: styles.fontSize.pdf.sm,
                  color: styles.colors.colorTitle,
                  bold: true,
                  alignment: "right",
                },
              ],
              ...registers.detalle.map((register) => [
                {
                  text: register.cantidad_impresion ?? register.cantidad,
                  fontSize: styles.fontSize.pdf.xsm,
                  color: styles.colors.colorDescription,
                  border: [true, false, false, false],
                  valign: "middle",
                },
                {
                  text: `${register.nombre_producto}${buildCompactProductInfo(
                    register,
                  )}`,
                  fontSize: styles.fontSize.pdf.xsm,
                  color: styles.colors.colorDescription,
                  border: [false, false, false, false],
                  valign: "middle",
                },
                {
                  text:
                    register.prefijo !== null
                      ? register.prefijo
                      : register.unidad_de_medida,
                  fontSize: styles.fontSize.pdf.xsm,
                  color: styles.colors.colorDescription,
                  border: [false, false, false, false],
                  valign: "middle",
                },
                {
                  text:
                    parseFloat(register.cantidad) < 1
                      ? money() +
                        parseFloat(register.cantidad) *
                          parseFloat(register.precio_de_venta).toFixed(2)
                      : money() + register.precio_de_venta,
                  fontSize: styles.fontSize.pdf.xsm,
                  color: styles.colors.colorDescription,
                  border: [false, false, false, false],
                  valign: "middle",
                },
                {
                  text: money() + format(register.subtotal),
                  fontSize: styles.fontSize.pdf.xsm,
                  color: styles.colors.colorDescription,
                  border: [false, false, false, false],
                  valign: "middle",
                },
                {
                  text: money() + format(register.igv),
                  fontSize: styles.fontSize.pdf.xsm,
                  color: styles.colors.colorDescription,
                  border: [false, false, false, false],
                  valign: "middle",
                },
                {
                  text: money() + format(register.total),
                  fontSize: styles.fontSize.pdf.xsm,
                  color: styles.colors.colorDescription,
                  bold: true,
                  border: [false, false, true, false],
                  valign: "middle",
                  alignment: "right",
                },
              ]), // Datos dinámicos
            ],
          },
          //! Margen para separar el encabezado con el detalle del comprobante
          margin: [0, 10, 0, 0],

          //* Color de los bordes de la celda
          layout: {
            hLineWidth: function (i, node) {
              return i === 0 || i === node.table.body.length ? 1 : 1;
            },
            vLineWidth: function (i, node) {
              return i === 0 || i === node.table.widths.length ? 1 : 1;
            },
            hLineColor: function (i, node) {
              return i === 0 || i === node.table.body.length
                ? styles.colors.borderColor
                : styles.colors.borderColor;
            },
            vLineColor: function (i, node) {
              return i === 0 || i === node.table.widths.length
                ? styles.colors.borderColor
                : styles.colors.borderColor;
            },
          },
        },
        //? Total del comprobante
        buildTotalsSection(),
        {
          text: `Cant. items: ${getTotalItems(registers)}`,
          fontSize: styles.fontSize.pdf.xsm,
          color: styles.colors.colorTitle,
          bold: true,
          margin: [0, 4, 0, 6],
          alignment: "right",
        },

        //?Informacion resumida del cajero
        {
          text: [
            "CAJER@: ".toUpperCase(),
            (registers.usuario.nombres ?? "").toUpperCase(),
            "    |    IMPORTE: ".toUpperCase(),
            money() + format(registers.orden.importe_de_pago),
            "    |    DEVUELTA: ".toUpperCase(),
            money() + format(registers.orden.vuelto_de_importe_de_pago),
          ].join(""),
          fontSize: styles.fontSize.pdf.xsm,
          color: styles.colors.colorTitle,
          bold: true,
          margin: [0, 6, 0, 8],
          alignment: "left",
        },
        {
          text: "Mensaje de agradecimiento:",
          fontSize: styles.fontSize.pdf.xsm,
          color: styles.colors.colorTitle,
          bold: true,
          margin: [0, 5, 0, 0],
          alignment: "left",
        },
        {
          text: registers.tipo_comprobante.mensaje_de_agradecimiento,
          fontSize: styles.fontSize.pdf.xsm,
          color: styles.colors.colorTitle,
          margin: [0, 0, 0, 0],
          alignment: "left",
        },
        {
          text: "Información:",
          fontSize: styles.fontSize.pdf.xsm,
          color: styles.colors.colorTitle,
          bold: true,
          margin: [0, 5, 0, 0],
          alignment: "left",
        },
        {
          text: registers.tipo_comprobante.mensaje_de_informacion,
          fontSize: styles.fontSize.pdf.xsm,
          color: styles.colors.colorTitle,
          margin: [0, 0, 0, 0],
          alignment: "left",
        },
        {
          text: registers.detalle.some(
            (producto) => producto.vencimiento !== null,
          )
            ? "Términos de garantía:"
            : "",
          fontSize: styles.fontSize.pdf.xsm,
          color: styles.colors.colorTitle,
          bold: true,
          margin: [0, 5, 0, 0],
          alignment: "left",
        },
        {
          text: registers.detalle.some(
            (producto) => producto.vencimiento !== null,
          )
            ? registers.tipo_comprobante.terminos
            : "",
          fontSize: styles.fontSize.pdf.xsm,
          color: styles.colors.colorTitle,
          margin: [0, 0, 0, 0],
          alignment: "left",
        },

        buildStandaloneQrSection(),

        //Firma del cliente
        {
          text: "--------------------                                   --------------------",
          fontSize: styles.fontSize.pdf.sm,
          color: styles.colors.colorTitle,
          bold: true,
          margin: [0, 15, 0, 0],
          alignment: "center",
        },
        {
          text: "           EMISOR                                                   RECEPTOR",
          fontSize: styles.fontSize.pdf.xsm,
          color: styles.colors.colorTitle,
          bold: true,
          margin: [0, -5, 0, 0],
          alignment: "center",
        },
      ],
    };
    // pdfMake.createPdf(docDefinition).print();

    const pdfDocGenerator = pdfMake.createPdf(docDefinition);
    await createElementPDF(pdfDocGenerator, {
      enablePrintButton: false,
      focusPrintButton: false,
      enterToPrint: true,
      closeOnEscape: true,
      autoPrintOnLoad: true,
      hidePreview: true,
      minimalUI: true,
      autoCloseAfterPrint: true,
      managedPrintTab: true,
    });
  } catch (error) {
    errorActions("No se pudo generar el comprobante");
  }
};
